import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragOverEvent,
  DragStartEvent,
  MouseSensor,
  pointerWithin,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useTranslation } from 'react-i18next';
import { EConfirmDialog, EDropCategory } from '../../data/Constants';
import {
  deepCloneSections,
  findSectionById,
  isTargetMultiNumeric,
} from '../../services/CategoryHelper';
import { getFeatureFamilyList, getViewData } from '../../store/states/CategorySlice';
import { AppDispatch, AppState } from '../../store/AppStore';
import AddCategoryComponent from './components/AddCategoryComponent';
import { CategoryDragOverlay } from './components/CategoryDragOverlay';
import { CategoryHeader } from './components/CategoryHeader';
import { CategoryPanes } from './components/CategoryPanes';
import ConfirmationDialog from '../ConfirmationDialog';
import { INITIAL_CONFIRM_DIALOG } from '../../../types/ICategoryTypes';
import {
  addCategorySection,
  confirmCategoryAction,
  removeCategoryItem,
  saveCategoryView,
} from './utils/categoryActionOperations';
import {
  collectAssignedCodes,
  getActiveUnassignedItems,
  getBreadcrumbSection,
} from './utils/categoryTreeUtils';
import {
  getCategoryDragData,
  handleCategoryDragEnd,
  isForbiddenCategoryDrop,
  isValidNumericItem as validateNumericItem,
} from './utils/categoryDragOperations';

import type { IBreadcrumbSection, IFeatureFamily, ISection } from '../../../types';
import type {
  IActiveDragItem,
  ICategoryConfirmDialogInfo,
  ICategoryDndSource,
} from '../../../types/ICategoryTypes';

const Category: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const categoryState = useSelector((state: AppState) => state.category);
  const product = useSelector((state: AppState) => state.procos.product);

  const [sections, setSections] = useState<ISection[]>([]);
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const [globalUnassignedItems, setGlobalUnassignedItems] = useState<IFeatureFamily[]>([]);
  const [isValidDragItem, setIsValidDragItem] = useState<boolean>(true);
  const [breadCrumb, setBreadCrumb] = useState<IBreadcrumbSection>();
  const [confirmDelete, setConfirmDelete] =
    useState<ICategoryConfirmDialogInfo>(INITIAL_CONFIRM_DIALOG);
  const [sectionToDelete, setSectionToDelete] = useState<ISection | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [active, setActive] = useState<IActiveDragItem>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string[]>([]);
  const [showForbidden, setShowForbidden] = useState(false);
  const [editDataView, setEditDataView] = useState(false);
  const paneRefs = useRef<HTMLDivElement[]>([]);

  const selectedSection = useMemo(
    () => (selectedSectionId ? findSectionById(sections, selectedSectionId) : null),
    [sections, selectedSectionId]
  );
  const activeData = useMemo(() => getCategoryDragData(active), [active]);
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 100, tolerance: 8 } })
  );

  const calculateUnAssigned = (sectionsToProcess: ISection[]) => {
    setSections(sectionsToProcess);
    const assignedCodes = collectAssignedCodes(sectionsToProcess);
    setGlobalUnassignedItems(getActiveUnassignedItems(categoryState.featureFamily, assignedCodes));
  };

  useEffect(() => {
    if (!product?.id) return;
    void Promise.allSettled([
      dispatch(getViewData({ productCode: product.id })),
      dispatch(
        getFeatureFamilyList({
          productCode: product.id,
          includeScopeFeatures: false,
          workItemNum: '',
          page: 0,
          limit: 0,
        })
      ),
    ]);
  }, [dispatch, product?.id]);

  useEffect(() => {
    if (!categoryState?.view?.sections) return;
    const rootSections = deepCloneSections(categoryState.view.sections) || [];
    if (rootSections.length > 0 && !selectedSectionId) {
      setSelectedSectionId(rootSections[0].code);
    }
    calculateUnAssigned(rootSections);
  }, [categoryState.view?.sections, categoryState.featureFamily]);

  useEffect(() => {
    if (!selectedSectionId) return;
    const nextBreadCrumb = getBreadcrumbSection(sections, selectedSectionId);
    if (nextBreadCrumb) setBreadCrumb(nextBreadCrumb);
  }, [selectedSectionId, sections]);

  const resizeHandler = (newWidths: number[]) => {
    if (paneRefs.current?.length === newWidths.length) {
      const totalWidth = newWidths.reduce((sum, width) => sum + width, 0);
      paneRefs.current.forEach((pane, index) => {
        pane.style.setProperty('--pane-width', `${(newWidths[index] / totalWidth) * 100}%`);
      });
    }
  };

  const setPaneRef = (index: number) => (element: HTMLDivElement | null) => {
    if (element) {
      paneRefs.current[index] = element;
    }
  };

  const isValidNumericItem = (over: ICategoryDndSource): boolean =>
    validateNumericItem(activeData, over, selectedSection);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
    setActive(event.active);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { over } = event;
    if (!over) return;
    const overData = getCategoryDragData(over);
    setShowForbidden(isForbiddenCategoryDrop(sections, activeData, overData, dispatch, t));
    const isDraggingItem =
      activeData?.container === EDropCategory.Assigned ||
      activeData?.container === EDropCategory.Unassigned;
    if (isDraggingItem) {
      setOverId(overData?.container ?? null);
      setIsValidDragItem(isTargetMultiNumeric(over) ? isValidNumericItem(over) : true);
    } else {
      setIsValidDragItem(true);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    setActive(null);
    setOverId(null);
    handleCategoryDragEnd(event, {
      dispatch,
      globalUnassignedItems,
      isValidNumericItem,
      sections,
      selectedSection,
      setGlobalUnassignedItems,
      setIsValidDragItem,
      setSections,
      t,
    });
  };

  const handleDeleteSection = (section: ISection) => {
    setSectionToDelete(section);
    setConfirmDelete({
      open: true,
      title: 'messages.category.confirm',
      description: 'messages.category.confirmDelete',
      alertFor: EConfirmDialog.DeleteSection,
    });
  };

  const cancelView = () =>
    setConfirmDelete({
      open: true,
      title: 'messages.category.unsavedChanges',
      description: 'messages.category.discardChanges',
      alertFor: EConfirmDialog.DiscardChanges,
    });
  const handleSave = () =>
    saveCategoryView({
      categoryState,
      dispatch,
      productId: product?.id,
      sections,
      setEditDataView,
    });
  const handleAddSection = (parent?: ISection | null) =>
    addCategorySection({ categoryState, dispatch, parent, productId: product?.id, sections });
  const handleRemoveItem = (itemCode: string) =>
    removeCategoryItem({
      categoryState,
      dispatch,
      itemCode,
      sections,
      selectedSectionId,
      setGlobalUnassignedItems,
      setSections,
    });

  const onConfirmAction = (confirm: { status: boolean; alertFor: string }) =>
    confirmCategoryAction({
      calculateUnAssigned,
      categoryState,
      confirm,
      dispatch,
      productId: product?.id,
      sectionToDelete,
      sections,
      selectedSectionId,
      setConfirmDelete,
      setEditDataView,
      setSectionToDelete,
      setSelectedSectionId,
    });

  return (
    <div className="category">
      <CategoryHeader
        breadCrumb={breadCrumb}
        cancelView={cancelView}
        editDataView={editDataView}
        handleSave={handleSave}
        isCategoryDataModified={categoryState.isCategoryDataModified}
        navigateToCategory={(section) => setSelectedSectionId(section.code)}
        onAddRootSection={() => handleAddSection()}
        onEnableEditView={() => setEditDataView(true)}
        t={t}
      />
      <DndContext
        sensors={sensors}
        onDragEnd={handleDragEnd}
        onDragOver={handleDragOver}
        onDragStart={handleDragStart}
        collisionDetection={pointerWithin}
      >
        <CategoryPanes
          editDataView={editDataView}
          expanded={expanded}
          globalUnassignedItems={globalUnassignedItems}
          handleAddSection={handleAddSection}
          handleDeleteSection={handleDeleteSection}
          handleRemoveItem={handleRemoveItem}
          isValidDragItem={isValidDragItem}
          overId={overId}
          paneRefs={paneRefs}
          resizeHandler={resizeHandler}
          sections={sections}
          selectedSection={selectedSection}
          selectedSectionId={selectedSectionId}
          setExpanded={setExpanded}
          setPaneRef={setPaneRef}
          setSelectedSectionId={setSelectedSectionId}
          t={t}
        />
        <DragOverlay>
          <CategoryDragOverlay
            activeData={activeData}
            activeId={activeId}
            showForbidden={showForbidden}
          />
        </DragOverlay>
      </DndContext>
      {categoryState.showAddCategoryDialog && (
        <AddCategoryComponent open={categoryState.showAddCategoryDialog} />
      )}
      {confirmDelete.open && (
        <ConfirmationDialog dialogInfo={confirmDelete} onConfirmAction={onConfirmAction} />
      )}
    </div>
  );
};

export default Category;
